import React, { useState, useEffect } from 'react';
import { uploadImage } from '../services/api';
import './PropertyForm.css';

const PropertyForm = ({ property, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        location: '',
        price: '',
        type: 'apartment',
        bedrooms: '',
        bathrooms: '',
        area: '',
        image: '🏢',
        badge: 'New',
        imageUrl: ''
    });

    const [errors, setErrors] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (property) {
            setFormData({
                title: property.title || '',
                location: property.location || '',
                price: property.price || '',
                type: property.type || 'apartment',
                bedrooms: property.bedrooms || '',
                bathrooms: property.bathrooms || '',
                area: property.area || '',
                image: property.image || '🏢',
                badge: property.badge || 'New',
                imageUrl: property.imageUrl || property.image_url || ''
            });
            if (property.imageUrl || property.image_url) {
                setImagePreview(property.imageUrl || property.image_url);
            }
        }
    }, [property]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'price' || name === 'bedrooms' || name === 'bathrooms' || name === 'area' 
                ? (value === '' ? '' : Number(value)) 
                : value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!formData.location.trim()) {
            newErrors.location = 'Location is required';
        }
        if (!formData.price || formData.price <= 0) {
            newErrors.price = 'Valid price is required';
        }
        if (formData.bedrooms === '' || formData.bedrooms < 0) {
            newErrors.bedrooms = 'Valid number of bedrooms is required';
        }
        if (formData.bathrooms === '' || formData.bathrooms < 0) {
            newErrors.bathrooms = 'Valid number of bathrooms is required';
        }
        if (!formData.area || formData.area <= 0) {
            newErrors.area = 'Valid area is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.match('image.*')) {
                alert('Please select an image file');
                return;
            }
            // Validate file size (5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should be less than 5MB');
                return;
            }
            setSelectedFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            try {
                setUploading(true);
                let finalImageUrl = formData.imageUrl;

                // Upload image if file is selected
                if (selectedFile) {
                    try {
                        const uploadResult = await uploadImage(selectedFile);
                        finalImageUrl = uploadResult.imageUrl;
                        setFormData(prev => ({ ...prev, imageUrl: finalImageUrl }));
                    } catch (uploadError) {
                        console.error('Image upload error:', uploadError);
                        alert('Error uploading image. Property will be saved without image.');
                    }
                }

                // Save property with image URL
                onSave({ ...formData, imageUrl: finalImageUrl });
            } catch (error) {
                console.error('Error in form submission:', error);
                alert('Error saving property. Please try again.');
            } finally {
                setUploading(false);
            }
        }
    };

    const removeImage = () => {
        setSelectedFile(null);
        setImagePreview(null);
        setFormData(prev => ({ ...prev, imageUrl: '' }));
    };

    const imageOptions = [
        { value: '🏢', label: 'Apartment' },
        { value: '🏡', label: 'Villa' },
        { value: '🏘️', label: 'Townhouse' },
        { value: '🏙️', label: 'Penthouse' },
        { value: '🏛️', label: 'Office' }
    ];

    const badgeOptions = ['New', 'Featured', 'Sale', 'Premium', 'Commercial', 'Luxury', 'Affordable'];

    return (
        <div className="property-form-container">
            <div className="property-form-header">
                <h2>{property ? 'Edit Property' : 'Add New Property'}</h2>
                <button onClick={onCancel} className="cancel-btn">
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <form onSubmit={handleSubmit} className="property-form">
                <div className="form-row">
                    <div className="form-group">
                        <label>Property Title *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="e.g., Luxury Apartment in Downtown"
                            required
                        />
                        {errors.title && <span className="error">{errors.title}</span>}
                    </div>

                    <div className="form-group">
                        <label>Location *</label>
                        <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="e.g., Downtown Dubai"
                            required
                        />
                        {errors.location && <span className="error">{errors.location}</span>}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Price (USD) *</label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            placeholder="e.g., 1250000"
                            min="0"
                            required
                        />
                        {errors.price && <span className="error">{errors.price}</span>}
                    </div>

                    <div className="form-group">
                        <label>Property Type *</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            required
                        >
                            <option value="apartment">Apartment</option>
                            <option value="villa">Villa</option>
                            <option value="townhouse">Townhouse</option>
                            <option value="penthouse">Penthouse</option>
                            <option value="office">Office</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Bedrooms *</label>
                        <input
                            type="number"
                            name="bedrooms"
                            value={formData.bedrooms}
                            onChange={handleChange}
                            placeholder="e.g., 3"
                            min="0"
                            required
                        />
                        {errors.bedrooms && <span className="error">{errors.bedrooms}</span>}
                    </div>

                    <div className="form-group">
                        <label>Bathrooms *</label>
                        <input
                            type="number"
                            name="bathrooms"
                            value={formData.bathrooms}
                            onChange={handleChange}
                            placeholder="e.g., 2"
                            min="0"
                            required
                        />
                        {errors.bathrooms && <span className="error">{errors.bathrooms}</span>}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Area (sqft) *</label>
                        <input
                            type="number"
                            name="area"
                            value={formData.area}
                            onChange={handleChange}
                            placeholder="e.g., 1800"
                            min="0"
                            required
                        />
                        {errors.area && <span className="error">{errors.area}</span>}
                    </div>

                    <div className="form-group">
                        <label>Badge</label>
                        <select
                            name="badge"
                            value={formData.badge}
                            onChange={handleChange}
                        >
                            {badgeOptions.map(badge => (
                                <option key={badge} value={badge}>{badge}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Property Image</label>
                    <div className="image-upload-section">
                        <div className="image-upload-area">
                            {imagePreview ? (
                                <div className="image-preview-container">
                                    <img src={imagePreview} alt="Preview" className="image-preview" />
                                    <button type="button" onClick={removeImage} className="remove-image-btn">
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ) : (
                                <label className="image-upload-label">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="upload-placeholder">
                                        <i className="fas fa-cloud-upload-alt"></i>
                                        <p>Click to upload image</p>
                                        <span>JPG, PNG, GIF, WEBP (Max 5MB)</span>
                                    </div>
                                </label>
                            )}
                        </div>
                        {selectedFile && (
                            <p className="file-name">{selectedFile.name}</p>
                        )}
                    </div>
                </div>

                <div className="form-group">
                    <label>Or Choose Icon</label>
                    <div className="image-options">
                        {imageOptions.map(option => (
                            <label 
                                key={option.value} 
                                className={`image-option ${formData.image === option.value ? 'selected' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="image"
                                    value={option.value}
                                    checked={formData.image === option.value}
                                    onChange={handleChange}
                                />
                                <span className="image-emoji">{option.value}</span>
                                <span className="image-label">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={onCancel} className="cancel-form-btn">
                        Cancel
                    </button>
                    <button type="submit" className="save-btn" disabled={uploading}>
                        <i className="fas fa-save"></i> {uploading ? 'Uploading...' : (property ? 'Update Property' : 'Add Property')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PropertyForm;
